<script setup lang="ts">
import type { PopoverContentEmits, PopoverContentProps } from 'reka-ui';
import { computed, inject, type HTMLAttributes, type Ref } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { PopoverContent, PopoverPortal, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

defineOptions({
    inheritAttrs: false,
});

const props = withDefaults(defineProps<PopoverContentProps & { class?: HTMLAttributes['class'] }>(), {
    align: 'center',
    sideOffset: 4,
});
const emits = defineEmits<PopoverContentEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

// our custom portal container
const portalContainerRef = inject<Ref<HTMLElement | null> | undefined>('portalContainerRef', undefined);
const collisionBoundary = computed<Element[]>(() => {
    const dialogBoundary = portalContainerRef?.value?.closest('dialog');
    return dialogBoundary ? [dialogBoundary] : [];
});

/****** START debug: uncomment if you need to debug the portal / teleport DOM
const updateDropdownPosition = () => {
    const portalRect = portalContainerRef?.value?.parentElement?.getBoundingClientRect();
    console.log('updateDropdownPosition', portalRect);
    if (!portalRect) {
        console.log('portalRect missing');
        return;
    }
    drawDebugBoundingBox(portalRect.top, portalRect.left, portalRect.bottom, portalRect.right, 'pink');
};

useEventListener('resize', updateDropdownPosition);

onMounted(() => {
    updateDropdownPosition();
});
 ***** END debug ****** */

// note: I added :to="portalContainerRef ? portalContainerRef : 'body'" because the dropdown wasn't visible if opened inside a dialog
</script>

<template>
    <PopoverPortal :to="portalContainerRef ? portalContainerRef : 'body'">
        <PopoverContent
            v-bind="{ ...forwarded, ...$attrs }"
            :collision-boundary="collisionBoundary"
            :class="
                cn(
                    // Canonical popover treatment (matches .user-menu and the
                    // datepicker panel): --overlay-surface for the ground and
                    // --ring-popover for the boundary — a soft drop shadow in
                    // light, hairline + strong shadow on dark chrome. No border:
                    // shadcn's bg-popover/border tokens are deliberately absent
                    // from default.css, and border-focus read as a wide outline.
                    'bg-[var(--overlay-surface)] text-primary [box-shadow:var(--ring-popover)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-70 w-72 rounded-lg p-4 outline-none',
                    props.class,
                )
            "
        >
            <slot />
        </PopoverContent>
    </PopoverPortal>
</template>
